import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  IDataTableFieldConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldParseIntoSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  parseRequiredValidatorParseFieldIntoModel,
  parseMaxAndMinimumSelectionValidatorFieldsIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setRequiredValidatorUpdateFieldFromModel,
  setMaxAndMinSelectionFormDataIntoField,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { IFormFieldsValidationModel } from 'src/app/core/models/form-fields-formly-validation.model';

const PAGINATION_DEFAULTS = {
  currentPageKey: 'page',
  pageSize: 20,
  collectionSizeKey: 'totalElements',
  pageSizeKey: 'size',
  sortKey: 'sort',
  sortDirectionKey: 'sortDirection',
  minimalPagination: false,
  zeroBasedPageIndex: true,
};

export const defaultDataTableConfigFormModel: IDataTableFieldConfigurationFormModel =
  {
    ...fieldsBaseConfigurationFormModel(),
    mainConfiguration: {
      title: 'Title of the table',
      selectable: false,
      columns: [
        {
          key: 'serviceName',
          title: 'Service Name',
          sortable: false,
        },
        {
          key: 'priceType',
          title: 'Price Type',
          sortable: false,
        },
      ],
      asyncData: false,
      searchable: true,
    },
    rowItems: {
      items: [],
    },
    paginationConfiguration: {
      sortKey: PAGINATION_DEFAULTS.sortKey,
      sortDirectionKey: PAGINATION_DEFAULTS.sortDirectionKey,
      collectionSizeKey: PAGINATION_DEFAULTS.collectionSizeKey,
      pageSizeKey: PAGINATION_DEFAULTS.pageSizeKey,
      currentPageKey: PAGINATION_DEFAULTS.currentPageKey,
      pageSize: PAGINATION_DEFAULTS.pageSize,
      minimalPagination: PAGINATION_DEFAULTS.minimalPagination,
      zeroBasedPageIndex: PAGINATION_DEFAULTS.zeroBasedPageIndex,
    },
  };

export const dataTableFieldGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultDataTableConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        ...generateDataTableMainAndDatasetConfigurationFields(),
        ...generateDataTableSearchConfigurationFields(),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        // ...generateDataTableSelectValidationFields(formBuilderServiceInstance),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const dataTableFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IDataTableFieldConfigurationFormModel => {
  const model: IDataTableFieldConfigurationFormModel =
    baseFieldParseIntoSettingsFormModel(
      field
    ) as unknown as IDataTableFieldConfigurationFormModel;

  model.mainConfiguration = {
    title: field.props?.['title'],
    columns: field.props?.['columns'],
    asyncData: field.props?.['asyncData'],
    searchable: field.props?.['searchable'],
    selectable: field.props?.['selectable'],
    emptyMessage: field.props?.['emptyMessage'],
    customTableClass: field.props?.['customTableClass'],
  };

  model.rowItems = {
    items: field.props?.['items'],
  };

  model.paginationConfiguration = {
    currentPageKey:
      field.props?.['currentPageKey'] ?? PAGINATION_DEFAULTS.currentPageKey,
    pageSize: field.props?.['pageSize'] ?? PAGINATION_DEFAULTS.pageSize,
    collectionSizeKey:
      field.props?.['collectionSizeKey'] ??
      PAGINATION_DEFAULTS.collectionSizeKey,
    pageSizeKey:
      field.props?.['pageSizeKey'] ?? PAGINATION_DEFAULTS.pageSizeKey,
    sortKey: field.props?.['sortKey'] ?? PAGINATION_DEFAULTS.sortKey,
    sortDirectionKey:
      field.props?.['sortDirectionKey'] ?? PAGINATION_DEFAULTS.sortDirectionKey,
    minimalPagination:
      field.props?.['minimalPagination'] ??
      PAGINATION_DEFAULTS.minimalPagination,
    zeroBasedPageIndex:
      field.props?.['zeroBasedPageIndex'] ??
      PAGINATION_DEFAULTS.zeroBasedPageIndex,
  };

  if (model.mainConfiguration?.asyncData) {
    const fieldDataset = field.props?.['dataset'] || {};

    const { sort, sortDirection, ...extraParamsItems } =
      fieldDataset?.['params'] ?? {};
    const extraParams = Object.keys(extraParamsItems || {}).map(key => ({
      key,
      value: extraParamsItems[key],
    }));

    const paramsDatasetId =
      fieldDataset?.['httpMethod'] === 'GET' &&
      fieldDataset?.['fetchMethod'] === 'datasetId'
        ? fieldDataset?.['params']?.['datasetId']
        : undefined;

    model.dataset = {
      fetchMethod: fieldDataset?.['fetchMethod'],
      httpMethod: fieldDataset?.['httpMethod'],
      endpointCode:
        fieldDataset?.['fetchMethod'] === 'endpointCode'
          ? fieldDataset?.['endpointCode']
          : undefined,
      datasetId: paramsDatasetId,
      customEndpointCode:
        fieldDataset?.['fetchMethod'] === 'customEndpointCode'
          ? fieldDataset?.['customEndpointCode']
          : undefined,
      url: fieldDataset?.['url'],
      requestHeaders: {
        headers: fieldDataset?.['headers'] ?? [],
      },
      dataField: fieldDataset?.['dataField'],
      useBaseUrl: fieldDataset?.['useBaseUrl'],
      params: {
        ...extraParams,
        sort,
        sortDirection,
      },
    };
  }

  if (model.mainConfiguration?.searchable) {
    model.searchConfiguration = {
      searchPlaceHolder: field.props?.['searchPlaceHolder'],
      searchKeywordKey: field.props?.['searchKeywordKey'],
    };
  }
  return model;
};

export const dataTableFieldUpdateFromSettingsFormModel = (
  model: IDataTableFieldConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = baseFieldUpdateFromSettingsFormModel(model, field);

  const fieldPropsClone = field?.props ? { ...field.props } : {};

  fieldPropsClone['title'] = model.mainConfiguration?.title;
  fieldPropsClone['emptyMessage'] = model?.mainConfiguration?.emptyMessage;
  fieldPropsClone['asyncData'] = model.mainConfiguration?.asyncData ?? false;
  fieldPropsClone['searchable'] = model.mainConfiguration?.searchable ?? false;
  fieldPropsClone['selectable'] = model.mainConfiguration?.selectable ?? false;
  fieldPropsClone['customTableClass'] =
    model.mainConfiguration?.customTableClass;
  fieldPropsClone['columns'] = model?.mainConfiguration?.columns ?? [];
  fieldPropsClone['items'] = model?.rowItems?.items ?? [];

  fieldPropsClone['searchPlaceHolder'] =
    model?.searchConfiguration?.searchPlaceHolder;
  fieldPropsClone['searchKeywordKey'] =
    model?.searchConfiguration?.searchKeywordKey;

  fieldPropsClone['minimalPagination'] =
    model?.paginationConfiguration?.minimalPagination ??
    PAGINATION_DEFAULTS.minimalPagination;
  fieldPropsClone['pageSize'] =
    model?.paginationConfiguration?.pageSize ?? PAGINATION_DEFAULTS.pageSize;
  fieldPropsClone['pageSizeKey'] =
    model?.paginationConfiguration?.pageSizeKey ??
    PAGINATION_DEFAULTS.pageSizeKey;
  fieldPropsClone['zeroBasedPageIndex'] =
    model?.paginationConfiguration?.zeroBasedPageIndex ??
    PAGINATION_DEFAULTS.zeroBasedPageIndex;
  fieldPropsClone['currentPageKey'] =
    model?.paginationConfiguration?.currentPageKey ??
    PAGINATION_DEFAULTS.currentPageKey;
  fieldPropsClone['collectionSizeKey'] =
    model?.paginationConfiguration?.collectionSizeKey ??
    PAGINATION_DEFAULTS.collectionSizeKey;
  fieldPropsClone['sortKey'] =
    model?.paginationConfiguration?.sortKey ?? PAGINATION_DEFAULTS.sortKey;
  fieldPropsClone['sortDirectionKey'] =
    model?.paginationConfiguration?.sortDirectionKey ??
    PAGINATION_DEFAULTS.sortDirectionKey;

  const dataSetParams: Record<string, string | undefined> = {};
  const modelExtraParamsItems: { key: string; value: string }[] | undefined =
    model?.dataset?.params?.extraParams;

  if (modelExtraParamsItems && modelExtraParamsItems.length > 0) {
    modelExtraParamsItems.forEach(item => {
      if (item?.key && item?.value) {
        dataSetParams[item.key] = item.value;
      }
    });
  }

  if (
    model?.dataset?.httpMethod === 'GET' &&
    model?.dataset?.fetchMethod === 'datasetId'
  ) {
    dataSetParams['datasetId'] = model?.dataset?.datasetId;
  }
  dataSetParams['sort'] = model?.dataset?.params?.sort;
  dataSetParams['sortDirection'] = model?.dataset?.params?.sortDirection;

  // delete undefined values and empty strings from dataSetParams
  Object.keys(dataSetParams).forEach(key => {
    if (dataSetParams[key] === undefined || dataSetParams[key] === '') {
      delete dataSetParams[key];
    }
  });

  fieldPropsClone['dataset'] = {
    fetchMethod: model?.dataset?.fetchMethod ?? 'datasetId',
    httpMethod: model?.dataset?.httpMethod || 'GET',
    endpointCode:
      model?.dataset?.fetchMethod === 'customEndpointCode'
        ? model?.dataset?.customEndpointCode
        : model?.dataset?.endpointCode,
    customEndpointCode: model?.dataset?.customEndpointCode,
    url: model?.dataset?.url,
    headers: model?.dataset?.requestHeaders?.headers || [],
    dataField: model?.dataset?.dataField,
    useBaseUrl: model?.dataset?.useBaseUrl ?? true,
    params: { ...dataSetParams },
  };

  field.props = { ...fieldPropsClone };

  return field;
};

export const dataTableParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseMaxAndMinimumSelectionValidatorFieldsIntoModel(model, field);

  return model;
};

export const dataTableFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = setRequiredValidatorUpdateFieldFromModel(model, field);
  const fieldPropsClone = field?.props ? { ...field.props } : {};
  fieldPropsClone['minSelection'] = model?.minSelection;
  fieldPropsClone['maxSelection'] = model?.maxSelection;

  field.props = { ...fieldPropsClone };
  field = setMaxAndMinSelectionFormDataIntoField(model, field);

  return field;
};

export const dataTableFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const dataTableFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

function updateRepeaterFields(
  repeaterField: FormlyFieldConfig,
  newFields: FormlyFieldConfig[],
  model: any[] = []
) {
  const newKeys = newFields.map(f => f.key);

  model.forEach((itemModel: any) => {
    // Remove old keys that are no longer in new config
    Object.keys(itemModel).forEach((k: any) => {
      if (!newKeys.includes(k)) {
        delete itemModel[k];
      }
    });

    // Add missing keys with default values
    newFields.forEach((field: FormlyFieldConfig) => {
      if (!(<string>field.key in itemModel)) {
        itemModel[<string>field.key] = field.defaultValue ?? null;
      }
    });
  });

  (<FormlyFieldConfig>repeaterField.fieldArray).fieldGroup = newFields;
  <FormlyFieldConfig>repeaterField.options?.build?.(); // important
}

export const generateDataTableMainAndDatasetConfigurationFields =
  (): FormlyFieldConfig[] => {
    const mainConfiguration: FormlyFieldConfig = {
      key: 'mainConfiguration',
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Data Configuration',
        required: false,
      },
    };
    mainConfiguration.fieldGroup = [
      {
        key: 'title',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Table title',
          placeholder: 'Enter table title',
          required: false,
        },
      },
      {
        key: 'emptyMessage',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Empty Message',
          placeholder: 'Enter empty message',
          required: false,
        },
      },
      {
        key: 'customTableClass',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Custom Table Class',
          placeholder: 'Enter custom table class',
          required: false,
        },
      },
      {
        key: 'asyncData',
        type: EIremboFormlyFieldTypes.checkbox,
        defaultValue: false,
        props: {
          label: 'Async Data Fetch',
          placeholder: 'Enable Async Data Fetch',
          required: false,
          formCheck: 'switch',
        },
      },
      {
        key: 'columns',
        type: EIremboFormlyFieldTypes.customrepeater,
        className: 'data_table_columns_repeater_field',
        props: {
          addText: 'Add Column',
          label: 'Table Columns',
          placeholder: 'Enter table columns',
        },
        hooks: {
          onInit: (field: FormlyFieldConfig | undefined) => {
            field?.formControl?.valueChanges?.subscribe(
              (
                value:
                  | undefined
                  | { title: string; key: string; sortable: boolean }[]
              ) => {
                const repeaterField =
                  field?.parent?.parent?.get?.('rowItems.items');

                const model = repeaterField?.formControl?.value || [];

                if (repeaterField?.fieldArray) {
                  repeaterField.fieldArray = {
                    wrappers: ['panel-wrapper'],
                    expressions: {
                      'props.label': '"Row item " + (Number(field?.key) + 1)',
                    },
                  };
                  const newFields: FormlyFieldConfig[] | undefined = value?.map(
                    (column: {
                      title: string;
                      key: string;
                      sortable: boolean;
                    }) => ({
                      key: column.key,
                      type: EIremboFormlyFieldTypes.input,
                      props: {
                        label: column.title,
                        placeholder: `Enter value for ${column.title}`,
                      },
                    })
                  );
                  repeaterField.fieldGroup = [];
                  updateRepeaterFields(repeaterField, newFields ?? [], model);
                }
              }
            );
          },
        },
        fieldArray: {
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Column Configuration',
          },
          fieldGroup: [
            {
              key: 'key',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Key',
                placeholder: 'Key',
                required: true,
              },
              validation: {
                messages: {
                  required: 'Key is required',
                },
              },
            },
            {
              key: 'title',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Title',
                placeholder: 'Title',
                required: true,
              },
              validation: {
                messages: {
                  required: 'Title is required',
                },
              },
            },
            {
              key: 'sortable',
              type: EIremboFormlyFieldTypes.checkbox,
              defaultValue: false,
              props: {
                label: 'Sortable',
                hideHeader: true,
                placeholder: 'Enable column sorting',
                required: false,
                formCheck: 'switch',
              },
            },
            {
              key: 'width',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Width',
                placeholder: 'Width',
              },
            },
          ],
          expressions: {
            'props.label': '"Column " + (Number(field?.key) + 1)',
          },
        },
      },
      {
        key: 'searchable',
        type: EIremboFormlyFieldTypes.checkbox,
        defaultValue: false,
        props: {
          label: 'Searchable table',
          hideHeader: true,
          placeholder: 'Enable searching on the table',
          required: false,
          formCheck: 'switch',
        },
      },
      {
        key: 'selectable',
        type: EIremboFormlyFieldTypes.checkbox,
        defaultValue: false,
        props: {
          label: 'Selectable',
          hideHeader: true,
          placeholder: 'Make table rows selectable',
          required: false,
          formCheck: 'switch',
        },
      },
    ];

    const rowItems: FormlyFieldConfig = {
      key: 'rowItems',
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Row Items',
      },
      fieldGroup: [
        {
          key: 'items',
          type: EIremboFormlyFieldTypes.customrepeater,
          props: {
            label: 'Row Items',
            placeholder: 'Enter row items',
            addText: 'Add row item',
          },
          fieldArray: {
            props: {
              label: 'Row Item Configuration',
            },
            wrappers: ['panel-wrapper'],
            fieldGroup: [
              {
                key: 'key',
                type: EIremboFormlyFieldTypes.input,
                props: {
                  label: 'Data Column 1 Value',
                  placeholder: 'Data Column 1 Value',
                },
              },
              {
                key: 'value',
                type: EIremboFormlyFieldTypes.input,
                props: {
                  label: 'Data Column 2 Value',
                  placeholder: 'Data Column 2 Value',
                },
              },
            ],
          },
          validation: {
            messages: {
              required: 'Row items are required',
            },
          },
        },
      ],
      expressions: {
        hide: '!(!field?.parent?.model?.mainConfiguration?.asyncData)',
      },
    };

    const dataset: FormlyFieldConfig = {
      key: 'dataset',
      wrappers: ['panel-wrapper'],
      className: 'data_table_dataset_field',
      props: {
        label: 'Configure Dataset',
      },
      fieldGroup: [
        {
          key: 'fetchMethod',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            label: 'Select Fetch Method',
            placeholder: 'Select fetch method',
            required: true,
            bindLabel: 'label',
            bindValue: 'value',
            options: [
              { label: 'Dataset Code', value: 'datasetId' },
              { label: 'Endpoint Code', value: 'endpointCode' },
              { label: 'Custom Endpoint Code', value: 'customEndpointCode' },
            ],
          },
          hooks: {
            onInit: (field: FormlyFieldConfig | undefined) => {
              field?.formControl?.valueChanges?.subscribe(
                (value: string | undefined) => {
                  const httpMethodField = field?.parent?.get?.('httpMethod');
                  const urlField = field?.parent?.get?.('url');
                  if (value === 'datasetId') {
                    httpMethodField?.formControl?.setValue('GET');
                    urlField?.formControl?.setValue('/admin/v1/dataset-items');
                  } else {
                    httpMethodField?.formControl?.setValue('POST');
                    urlField?.formControl?.setValue(
                      '/integration/v1/fetch/sync'
                    );
                  }
                }
              );
            },
          },
          validation: {
            messages: {
              required: 'Fetch method is required',
            },
          },
        },
        {
          key: 'customEndpointCode',
          type: EIremboFormlyFieldTypes.input,
          props: {
            label: 'Endpoint Code',
            placeholder: 'Endpoint Code',
            required: true,
          },
          expressions: {
            hide: 'model?.fetchMethod !== "customEndpointCode"',
          },
          validation: {
            messages: {
              required: 'Endpoint Code is required',
            },
          },
        },
        {
          key: 'endpointCode',
          type: EIremboFormlyFieldTypes.customdropdownpaginated,
          props: {
            useBaseUrl: true,
            label: 'Integration endpoint',
            placeholder: 'Integration endpoint',
            required: true,
            bindLabel: 'name',
            bindValue: 'code',
            options: [],
            dataset: {
              url: '/integration/v1/endpoints',
              params: {
                page: 0,
                size: 10,
                adapterCode: 'FFORWARD',
              },
              pagination: true,
              searching: true,
              dataField: 'data.content',
              searchField: 'name',
            },
          },
          validation: {
            messages: {
              required: 'Integration endpoint is required',
            },
          },
          expressions: {
            hide: 'model?.fetchMethod !== "endpointCode"',
          },
        },
        {
          key: 'datasetId',
          type: EIremboFormlyFieldTypes.customdropdownpaginated,
          props: {
            useBaseUrl: true,
            label: 'Datasets',
            placeholder: 'Datasets',
            required: true,
            bindLabel: 'name',
            bindValue: 'id',
            options: [],
            dataset: {
              url: '/admin/v1/datasets',
              params: {
                page: 0,
                size: 10,
                sortDirection: 'DESC',
                sort: 'createdDate',
              },
              pagination: true,
              searching: true,
              dataField: 'data.content',
              searchField: 'name',
            },
          },
          validation: {
            messages: {
              required: 'Dataset is required',
            },
          },
          expressions: {
            hide: 'model?.fetchMethod !== "datasetId"',
          },
        },
        {
          key: 'httpMethod',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            readonly: true,
            label: 'HTTP Method',
            placeholder: 'Select HTTP Method',
            required: true,
            bindLabel: 'label',
            bindValue: 'value',
            options: [
              { label: 'GET', value: 'GET' },
              { label: 'POST', value: 'POST' },
            ],
          },
          validation: {
            messages: {
              required: 'HTTP Method is required',
            },
          },
        },
        {
          key: 'url',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: '/admin/v1/dataset-items',
          props: {
            readonly: true,
            label: 'URL',
            placeholder: 'Enter URL',
            required: true,
          },
          expressions: {
            hideField: 'model?.fetchMethod !== "datasetId"',
          },
        },
        {
          key: 'requestHeaders',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Request Headers',
            required: false,
          },
          fieldGroup: [
            {
              key: 'headers',
              type: EIremboFormlyFieldTypes.customrepeater,
              wrappers: ['panel-wrapper'],
              props: {
                label: 'Request Headers',
                placeholder: 'Enter Header key and value',
                required: false,
                addText: 'Add header',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'key',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Key',
                      placeholder: 'Header Key',
                      required: true,
                    },
                    validation: {
                      messages: {
                        required: 'Header Key is required',
                      },
                    },
                  },
                  {
                    key: 'value',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Header Value',
                      placeholder: 'Header Value',
                      required: true,
                    },
                    validation: {
                      messages: {
                        required: 'Header Value is required',
                      },
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          key: 'dataField',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: 'data.content',
          props: {
            label: 'Data Field',
            placeholder: 'Data Field Path (e.g., data.content)',
            required: true,
          },
          validation: {
            messages: {
              required: 'Data Field is required',
            },
          },
        },
        {
          key: 'params',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Params',
            required: false,
          },
          fieldGroup: [
            {
              key: 'sort',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Sort Field',
                placeholder: 'Enter sort field (e.g., dateCreated)',
                required: false,
              },
            },
            {
              key: 'sortDirection',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Sort Direction',
                placeholder: 'Select sort direction',
                required: true,
                defaultValue: 'ASC',
              },
              expressions: {
                hide: '!model?.sort || model?.sort?.trim() === ""',
              },
            },
            {
              key: 'extraParams',
              type: EIremboFormlyFieldTypes.customrepeater,
              props: {
                label: 'Request Parameters',
                placeholder: 'Enter param key and value',
                required: false,
                addText: 'Add params',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'key',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Key',
                      placeholder: 'Param Key',
                      required: true,
                    },
                    validation: {
                      messages: {
                        required: 'Param Key is required',
                      },
                    },
                  },
                  {
                    key: 'value',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Param Value',
                      placeholder: 'Param Value',
                      required: true,
                    },
                    validation: {
                      messages: {
                        required: 'Param Value is required',
                      },
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          key: 'useBaseUrl',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: true,
          props: {
            hideField: true,
            readonly: true,
            label: 'Use Base URL',
            hideHeader: true,
            placeholder: 'Use Base URL from the application configuration',
            required: false,
            formCheck: 'switch',
          },
        },
      ],
      expressions: {
        hide: '!field?.parent?.model?.mainConfiguration?.asyncData',
      },
    };

    const paginationConfiguration: FormlyFieldConfig = {
      key: 'paginationConfiguration',
      wrappers: ['panel-wrapper'],
      className: 'data_table_dataset_field',
      props: {
        label: 'Configure pagination',
      },
      fieldGroup: [
        {
          key: 'currentPageKey',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: PAGINATION_DEFAULTS.currentPageKey,
          props: {
            label: 'Current page key',
            placeholder: 'Enter current page key',
            required: true,
            hint: `Default is "${PAGINATION_DEFAULTS.currentPageKey}"`,
          },
          validation: {
            messages: {
              required: `Current page key is required.`,
            },
          },
        },
        {
          key: 'pageSize',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: PAGINATION_DEFAULTS.pageSize,
          props: {
            type: 'number',
            label: 'Page size',
            placeholder: 'Enter page size',
            required: true,
            hint: `Default is ${PAGINATION_DEFAULTS.pageSize}`,
          },
          validation: {
            messages: {
              required: `Page size is required.`,
            },
          },
        },
        {
          key: 'pageSizeKey',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: PAGINATION_DEFAULTS.pageSizeKey,
          props: {
            label: 'Page size key',
            placeholder: 'Enter page size key ',
            required: true,
            hint: `Default is "${PAGINATION_DEFAULTS.pageSizeKey}"`,
          },
          validation: {
            messages: {
              required: `Page size key is required.`,
            },
          },
        },
        {
          key: 'collectionSizeKey',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: PAGINATION_DEFAULTS.collectionSizeKey,
          props: {
            label: 'Collection size key',
            placeholder: 'Enter collection size key',
            required: true,
            hint: `Default is "${PAGINATION_DEFAULTS.collectionSizeKey}"`,
          },
          validation: {
            messages: {
              required: `Collection size key is required.`,
            },
          },
        },
        {
          key: 'sortKey',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: PAGINATION_DEFAULTS.sortKey,
          props: {
            label: 'Sort key',
            placeholder: 'Enter sort key',
            required: false,
            hint: `Default is "${PAGINATION_DEFAULTS.sortKey}"`,
          },
        },
        {
          key: 'sortDirectionKey',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: PAGINATION_DEFAULTS.sortDirectionKey,
          props: {
            label: 'Sort direction key',
            placeholder: 'Enter sort direction key',
            required: false,
            hint: `Default is "${PAGINATION_DEFAULTS.sortDirectionKey}"`,
          },
        },
        {
          key: 'minimalPagination',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: PAGINATION_DEFAULTS.minimalPagination,
          props: {
            label: 'Minimal Pagination',
            hideHeader: true,
            placeholder: 'Enable Minimal Pagination',
            required: false,
            formCheck: 'switch',
            hint: 'If enabled, the pagination will be minimal with only next and previous buttons.',
          },
        },
        {
          key: 'zeroBasedPageIndex',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: PAGINATION_DEFAULTS.zeroBasedPageIndex,
          props: {
            label: 'Zero-based Page Index',
            hideHeader: true,
            placeholder: 'Enable zero-based Page Index',
            required: false,
            formCheck: 'switch',
            hint: 'If enabled, the page index starts from 0. Otherwise, it starts from 1.',
          },
        },
      ],
    };
    return [mainConfiguration, rowItems, dataset, paginationConfiguration];
  };

export const generateDataTableSearchConfigurationFields =
  (): FormlyFieldConfig[] => {
    const searchConfiguration: FormlyFieldConfig = {
      key: 'searchConfiguration',
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Search Configuration',
        required: false,
      },
      expressions: {
        hide: '!field?.parent?.model?.mainConfiguration?.searchable',
      },
    };
    searchConfiguration.fieldGroup = [
      {
        key: 'searchPlaceHolder',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Search placeholder',
          placeholder: 'Enter search placeholder',
          required: false,
        },
      },
      {
        key: 'searchKeywordKey',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Search keyword key',
          placeholder: 'Enter search keyword key',
          required: false,
        },
      },
    ];
    return [searchConfiguration];
  };

export const generateDataTableSelectValidationFields = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  if (
    formBuilderServiceInstance?.configureFieldAction$?.value?.props?.[
      'selectable'
    ] !== true
  ) {
    return {};
  }

  return {
    key: 'tableValidation',
    type: EIremboFormlyFieldTypes['formly-group'],
    fieldGroup: [
      {
        key: 'minSelection',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Minimum selection',
          placeholder: 'Minimum selection',
          required: false,
        },
      },
      {
        key: 'maxSelection',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Maximum selection',
          placeholder: 'Maximum selection',
          required: false,
        },
      },
    ],
  };
};
